import type {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig
} from "axios";
import config from "config/config";
import { instance } from "./apiBase";

const API_BASE = config.apiHost;

// Runtime
let accessToken: string | null = null;

const REFRESH_TOKEN_KEY = "portal_refresh_token";

// to prevent 401 loop refresh.
let isRefreshing = false;
let refreshPromise: Promise<void> | null = null;

interface JwtTokens {
  access_token: string;
  // Still returned in the body during the CRM-619 rollout, but the FE no
  // longer stores it — the refresh token lives in an httpOnly cookie.
  refresh_token?: string;
}

export const jwt = {
  getAccessToken(): string | null {
    return accessToken;
  },

  // Store the access token in memory only. The refresh token is managed by
  // the backend as an httpOnly cookie (CRM-619) and is never read by JS.
  setTokens(tokens: JwtTokens) {
    accessToken = tokens.access_token;
  },

  // Clear the in-memory access token. Also removes any legacy refresh token
  // left in localStorage by pre-CRM-619 builds.
  clearTokens() {
    accessToken = null;
    if (typeof window !== "undefined")
      try {
        localStorage.removeItem(REFRESH_TOKEN_KEY);
      } catch {
        // ignore
      }
  },

  // Ensure we have a valid access token in memory (may perform refresh).
  // The httpOnly cookie is sent automatically; refresh throws when no valid
  // session cookie is present, which callers treat as "logged out".
  async ensureAccessToken(): Promise<void> {
    if (accessToken) return;
    await jwt.refresh();
  },

  // Exchange the httpOnly refresh-token cookie for a new access token. The
  // backend rotates the cookie via Set-Cookie on success. `credentials:
  // "include"` is required for the cookie to be sent cross-origin.
  async refresh(): Promise<void> {
    if (isRefreshing && refreshPromise) return refreshPromise;

    isRefreshing = true;
    refreshPromise = fetch(`${API_BASE}/portal/v1/auth/jwt/refresh`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" }
    })
      .then(async (res) => {
        if (!res.ok) {
          // No valid session — drop the in-memory access token.
          jwt.clearTokens();
          throw new Error("Refresh failed");
        }
        const data: JwtTokens = await res.json();
        jwt.setTokens(data);
      })
      .finally(() => {
        isRefreshing = false;
        refreshPromise = null;
      });

    return refreshPromise;
  },

  async impersonate(token?: string): Promise<boolean> {
    if (!token) return false;

    const response = await instance.post<{
      access_token: string;
      refresh_token: string;
    }>(
      "/portal/v1/auth/jwt/exchange",
      { token_key: token },
      // Accept the Set-Cookie that rotates the httpOnly refresh token (CRM-619).
      { withCredentials: true }
    );

    const tokens = response.data;

    jwt.clearTokens();
    jwt.setTokens(tokens);

    return true;
  },

  // Attach axios interceptors for Authorization + auto-refresh
  attachInterceptors(client: AxiosInstance) {
    client.interceptors.request.use((cfg: InternalAxiosRequestConfig) => {
      const at = jwt.getAccessToken();
      if (at) cfg.headers.Authorization = `Bearer ${at}`;
      return cfg;
    });

    client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const { response, config: cfg } = error;
        if (!response || !cfg) throw error;

        // Avoid infinite loop
        // @ts-expect-error custom flag
        if (cfg.__isRetry) throw error;

        if (response.status === 401) {
          // @ts-expect-error custom flag
          if (cfg.skipAuthRefresh) throw error;
          // Track whether refresh itself succeeded so we only clear tokens
          // when the session is truly invalid, not when a re-auth endpoint
          // rejects a wrong password/OTP on the retried request.
          let refreshSucceeded = false;
          try {
            await jwt.refresh();
            refreshSucceeded = true;
            const at = jwt.getAccessToken();
            if (!at) throw error;
            // @ts-expect-error custom flag
            cfg.__isRetry = true;
            cfg.headers.Authorization = `Bearer ${at}`;
            return client(cfg);
          } catch {
            if (!refreshSucceeded) jwt.clearTokens();
            window.dispatchEvent(new CustomEvent("auth:session-expired"));
            throw error;
          }
        }

        throw error;
      }
    );
  }
};
